<template>
  <div>
    <div class="container">

      <PageTitle :title="$t('loyalty_transactions.reports_title')"></PageTitle>

      <Panel class="configurations" :title="$t('parameters')" collapsible>
        <div class="columns">
          <div class="column">
            <div class="group">
              <label>{{ $t('report_type') }}</label>
              <select v-model="report">
                <option value="transactions">{{ $t('transactions') }}</option>
                <option value="accounts">{{ $t('loyalty_accounts.report.balance') }}</option>
              </select>
            </div>
            <div class="group" v-show="isAccountReport">
              <label>{{ $t('date') }}</label>
              <datetime type="datetime" v-model="parameters.atTime" :use12-hour="hourFormat === '12'" value-zone="local"
                :input-class="{ invalid: $validate.error('parameters.atTime') }" input-style="width: 100%"
                :max-datetime="now" />
            </div>
            <div class="group" v-show="isTransactionReport">
              <label>{{ $t('start') }}</label>
              <datetime type="date" :use12-hour="hourFormat === '12'" v-model="parameters.start" value-zone="local"
                :input-class="{ invalid: $validate.error('parameters.start') }" input-style="width: 100%"
                :max-datetime="parameters.end" />
            </div>
            <div class="group" v-show="isTransactionReport">
              <label>{{ $t('end') }}</label>
              <datetime type="date" :use12-hour="hourFormat === '12'" v-model="parameters.end" value-zone="local"
                :input-class="{ invalid: $validate.error('parameters.end') }" input-style="width: 100%"
                :min-datetime="parameters.start" :max-datetime="now" />
            </div>
            <div class="group" v-show="isTransactionReport">
              <label>{{ $t('loyalty_transactions.type') }}</label>
              <select v-model="parameters.type">
                <option :value="undefined">{{ $t('all_f') }}</option>
                <option :value="t.id" v-for="t of filterableTypes">{{ $t(`loyalty_transactions.${t.id}`) }}</option>
              </select>
            </div>
          </div>

          <div class="column"></div>
        </div>

        <button class="btn btn-primary" @click="generate">{{ $t('generate') }}</button>
      </Panel>

      <Panel ref="dataTab" :title="$t('result')" v-if="tableData">
        <RawTable :columns="tableData.columns" :rows="tableData.rows" :keyValueHeader="queryOptions" />
      </Panel>

    </div>
  </div>
</template>

<script>
  import RawTable from '@/components/RawTable.vue';
  import { mapActions } from "vuex";
  import moment from "moment-timezone";
  import { DateTime } from "luxon";
  import PageTitle from "@/components/PageTitle";

  export default {
    name: "LoyaltyTransactionStat",
    components: { RawTable, PageTitle },

    data() {
      return {
        now: DateTime.local().toISO(),
        filterableTypes: [
          { id: 'sale' },
          { id: 'adjust' },
          { id: 'recharge' },
          { id: 'auto_recharge' },
          { id: 'recharge_bonus' },
          { id: 'transfer' },
        ],
        report: 'transactions',
        parameters: {
          start: moment().toISOString(),
          end: moment().toISOString(),
          atTime: moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toISOString(),
          type: undefined
        },
        queryOptions: null,
        tableData: null
      }
    },

    computed: {
      branchId() {
        return this.$route.query.branch;
      },
      isTransactionReport() {
        return this.report === 'transactions'
      },
      isAccountReport() {
        return this.report === 'accounts'
      },
      hourFormat() {
        if (this.$store.state.contextCompany) {
          return this.$store.state.contextCompany.hourFormat;
        }
        return "24";
      },
    },

    validate: {
      "parameters.start": {
        required: true
      },
      "parameters.end": {
        required: true
      },
      "parameters.atTime": {
        required: true
      }
    },

    mounted() {
      this.$store.state.pageTitleTranslationKey = "loyalty_transactions.reports_title";
      this.$store.state.pageIcon = "/icon/loyalty.svg";
    },

    methods: {
      ...mapActions(['reportLoyaltyTransactions', 'reportAccountBalances']),

      async generate() {
        if (!this.$validate.valid()) {
          this.$validate.validateAllOnce();
          return;
        }
        let response = null;
        if (this.isTransactionReport) {
          response = await this.report_transactions();
        } else if (this.isAccountReport) {
          response = await this.report_accounts();
        }
        if (response) {
          this.tableData = response.data;
          this.queryOptions = response.query;

          /* type translation */
          if (this.queryOptions.type) {
            this.queryOptions.type = { tr: `loyalty_transactions.${this.queryOptions.type}` }
          }
          setTimeout(() => {
            this.$refs.tabs.setActive(this.$refs.dataTab);
          }, 0);
        } else {
          toast(this.$t('error.no_server'), 'error');
        }
      },

      async report_transactions() {
        return await this.reportLoyaltyTransactions({
          options: {
            branch: this.branchId,
            start: this.parameters.start,
            end: this.parameters.end.replace('00:00:00', '23:59:59'),
            type: this.parameters.type
          }
        });
      },

      async report_accounts() {
        return await this.reportAccountBalances({
          options: {
            branch: this.branchId,
            atTime: this.parameters.atTime
          }
        });
      }
    }
  }
</script>

<style lang="scss" scoped>
  @media print {
    .configurations {
      display: none !important;
    }
  }
</style>
